import { create } from "zustand";
import { Swiper as SwiperClass } from "swiper/types";
import { StoryType, StorySlide } from "@core/types";

type StoriesState = {
  slidesData: StorySlide[][];
  isModalOpened: boolean;
  timer: ReturnType<typeof setTimeout> | null;
  startTimeMS: number;
  timeOut: number;
  parentSlider: SwiperClass | null;
  nestedSlider: SwiperClass | null;
  parentSliderActiveIndex: number;
  nestedSliderActiveIndex: number;
  isPlaying: boolean;
  isSliderLoading: boolean;
  activeVideo: HTMLVideoElement | null;
  isVideoMuted: boolean;
};

type StoriesActions = {
  setStoriesData: (stories: StoryType[]) => void;
  setIsModalOpened: () => void;
  setParentSlider: (slider: SwiperClass) => void;
  setNestedSlider: (slider: SwiperClass, id: number) => void;
  setIsSliderLoading: (status: boolean) => void;
  setInitialIndex: (index: number) => void;
  slideToNext: () => void;
  slideToPrev: () => void;
  setTimer: (callback: () => void, timeOut: number) => void;
  clearTimer: () => void;
  updateTimer: () => void;
  startPaginationAnimating: (slider: SwiperClass, timeOut: number) => void;
  stopPaginationAnimating: () => void;
  setBulletToStartPosition: (slider: SwiperClass) => void;
  setBulletToEndPosition: (slider: SwiperClass) => void;
  setIsSliderPlaying: (isPlaying: boolean) => void;
  resetStoriesState: () => void;
  toggleSliderPlaying: () => void;
  slideToNextBtnHandler: () => void;
  slideToPrevBtnHandler: () => void;
  setActiveVideo: (video: StoriesState["activeVideo"]) => void;
  toggleVideoVolume: () => void;
};

const DEFAULT_TIMEOUT = 5000;

const initialState: StoriesState = {
  slidesData: [],
  timer: null,
  isModalOpened: false,
  startTimeMS: new Date().getTime(),
  timeOut: DEFAULT_TIMEOUT,
  parentSlider: null,
  nestedSlider: null,
  isSliderLoading: false,
  isPlaying: true,
  parentSliderActiveIndex: 0,
  nestedSliderActiveIndex: 0,
  activeVideo: null,
  isVideoMuted: false,
};

const getActiveBulletEl = (slider: SwiperClass): HTMLElement => {
  const activeIndex = slider.activeIndex;
  return slider?.pagination.bullets[activeIndex].children[0] as HTMLElement;
};

export const useStoriesProviderState = create<StoriesState & StoriesActions>()((set, get) => ({
  ...initialState,

  setStoriesData: (stories) => {
    set((state) => {
      return {
        ...state,
        slidesData: [
          ...stories.map((item) => {
            return item.content;
          }),
        ],
      };
    });
  },

  setIsModalOpened: () => {
    const { activeVideo } = get();
    if (activeVideo) {
      activeVideo.pause();
    }

    set((state) => {
      return {
        ...state,
        isModalOpened: !state.isModalOpened,
      };
    });
  },

  setParentSlider: (ref) => {
    set((state) => {
      return {
        ...state,
        parentSlider: ref,
      };
    });
  },

  setNestedSlider: (swiper, id) => {
    set((state) => {
      return {
        ...state,
        nestedSlider: swiper,
        nestedSliderActiveIndex: id,
      };
    });
  },

  setActiveVideo: (video) => {
    set((state) => {
      return {
        ...state,
        activeVideo: video,
      };
    });
  },

  setInitialIndex: (index) => {
    set((state) => {
      return {
        ...state,
        parentSliderActiveIndex: index,
      };
    });
  },

  startPaginationAnimating: (slider, timeOut) => {
    const bullet = getActiveBulletEl(slider);
    bullet.style.transform = "translateX(0%)";
    bullet.style.transitionDuration = timeOut + "ms";
  },

  stopPaginationAnimating: () => {
    const { nestedSlider } = get();
    if (!nestedSlider) return;

    const bulletParent = nestedSlider?.pagination.bullets[nestedSlider.activeIndex];
    const bulletEl = bulletParent.children[0] as HTMLElement;

    const computedRightPosition = bulletParent?.getBoundingClientRect().right - bulletEl?.getBoundingClientRect().right;

    bulletEl.style.transform = `translateX(${-computedRightPosition}px)`;
  },

  setBulletToStartPosition: (slider) => {
    const bullet = getActiveBulletEl(slider);
    bullet.style.transform = "translateX(-100%)";
    bullet!.style.transitionDuration = 0 + "ms";
  },

  setBulletToEndPosition: (slider) => {
    const bullet = getActiveBulletEl(slider);
    bullet.style.transform = "translateX(0)";
    bullet!.style.transitionDuration = 0 + "ms";
  },

  setIsSliderLoading: (status) => {
    set((state) => {
      return {
        ...state,
        isSliderLoading: status,
      };
    });
  },
  clearTimer: () => {
    const { timer } = get();
    if (!timer) return;

    clearTimeout(timer);
    set((state) => {
      return {
        ...state,
        timer: null,
        startTimeMS: new Date().getTime(),
        timeOut: DEFAULT_TIMEOUT,
      };
    });
  },

  setTimer: (callback, timeOut) => {
    const { clearTimer } = get();
    clearTimer();

    set((state) => {
      return {
        ...state,
        timer: setTimeout(callback, timeOut),
        startTimeMS: new Date().getTime(),
        timeOut,
      };
    });
  },

  // startPlaying

  // setTimeOut

  setIsSliderPlaying: (isPlaying) => {
    set((state) => {
      return {
        ...state,
        isPlaying,
      };
    });
  },

  updateTimer: () => {
    const { timeOut, startTimeMS, clearTimer } = get();
    clearTimer();

    const currentTime = new Date().getTime();
    const newTimeOut = timeOut - (currentTime - startTimeMS);

    set((state) => {
      return {
        ...state,
        timeOut: newTimeOut,
      };
    });
  },

  slideToNextBtnHandler: () => {
    const { nestedSlider, clearTimer, setBulletToStartPosition, setBulletToEndPosition, slideToNext } = get();

    if (!nestedSlider) return;

    if (!nestedSlider.isEnd) {
      setBulletToEndPosition(nestedSlider);
    }

    if (nestedSlider.isEnd) {
      setBulletToStartPosition(nestedSlider);
    }
    clearTimer();
    slideToNext();
  },

  slideToPrevBtnHandler: () => {
    const { nestedSlider, parentSliderActiveIndex, slideToPrev, clearTimer, setBulletToStartPosition } = get();

    if (!nestedSlider) return;

    if (nestedSlider.isBeginning && parentSliderActiveIndex === 0) return;

    setBulletToStartPosition(nestedSlider);

    if (!nestedSlider.isBeginning && nestedSlider.slides?.length > 1) {
      const prevBullet = nestedSlider?.pagination.bullets[nestedSlider.activeIndex - 1].childNodes[0] as HTMLElement;

      prevBullet.style.transform = "translateX(-100%)";
      prevBullet.style.transitionDuration = 0 + "ms";
    }
    clearTimer();
    slideToPrev();
  },

  slideToNext: () => {
    const { parentSlider, nestedSlider, parentSliderActiveIndex, resetStoriesState, setActiveVideo } = get();
    if (!nestedSlider || !parentSlider) return;

    setActiveVideo(null);

    if (nestedSlider.isEnd) {
      const isLastParentSlide = parentSlider?.activeIndex === parentSlider?.slides.length - 1;
      if (isLastParentSlide) {
        resetStoriesState();
        return;
      }

      if (nestedSlider.slides.length > 1) {
        nestedSlider.slideReset(0);
      }

      parentSlider?.slideNext();
      set((state) => {
        return {
          ...state,
          parentSliderActiveIndex: parentSliderActiveIndex + 1,
          isSliderLoading: false,
          isVideoMuted: false,
        };
      });
      return;
    }

    nestedSlider?.slideNext();
  },

  slideToPrev: () => {
    const { parentSlider, nestedSlider, parentSliderActiveIndex, setActiveVideo } = get();
    if (!nestedSlider || !parentSlider) {
      return;
    }
    setActiveVideo(null);

    if (nestedSlider?.isBeginning) {
      if (parentSliderActiveIndex === 0) return;

      nestedSlider.slideReset(0);

      parentSlider?.slidePrev();

      set((state) => {
        return {
          ...state,
          parentSliderActiveIndex: parentSliderActiveIndex - 1,
          isSliderLoading: false,
          isVideoMuted: false,
        };
      });
      return;
    }
    nestedSlider?.slidePrev();
  },

  toggleSliderPlaying: () => {
    const {
      timeOut,
      nestedSlider,
      isPlaying,
      stopPaginationAnimating,
      setIsSliderPlaying,
      updateTimer,
      startPaginationAnimating,
      slideToNext,
      clearTimer,
      setBulletToStartPosition,
      setTimer,
      activeVideo,
    } = get();

    if (activeVideo) {
      if (activeVideo.paused) {
        const playPromise = activeVideo?.play();
        if (playPromise !== undefined) {
          playPromise
            .then((_) => {
              activeVideo?.play();
            })
            .catch((error) => {
              console.log(error, "error");
            });
        }
      } else {
        activeVideo?.pause();
      }

      if (isPlaying) {
        stopPaginationAnimating();
        setIsSliderPlaying(false);
        updateTimer();
      }
      return;
    }

    if (isPlaying) {
      stopPaginationAnimating();
      setIsSliderPlaying(false);
      updateTimer();
      return;
    }

    if (!nestedSlider) return;

    startPaginationAnimating(nestedSlider, timeOut);
    setIsSliderPlaying(true);
    if (nestedSlider.isEnd) {
      setTimer(() => {
        clearTimer();
        slideToNext();
        setBulletToStartPosition(nestedSlider);
      }, timeOut);
      return;
    }

    setTimer(() => {
      clearTimer();
      slideToNext();
    }, timeOut);
  },

  resetStoriesState: () => {
    const { clearTimer, parentSlider, nestedSlider, activeVideo } = get();

    if (activeVideo) {
      activeVideo.pause();
    }

    parentSlider?.destroy();
    nestedSlider?.destroy();

    clearTimer();
    set(initialState);
  },

  toggleVideoVolume: () => {
    set((state) => {
      return {
        ...state,
        isVideoMuted: !state.isVideoMuted,
      };
    });
  },
}));

const useStoriesProviderData = () => {
  return useStoriesProviderState((state) => ({
    ...state,
  }));
};

export { useStoriesProviderData };
